import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { ConversionAttempt } from "@/lib/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversionsFilters {
  status?: string | undefined;
  marketplace?: string | undefined;
  search?: string | undefined;
  dateFrom?: string | undefined;
  page: number;
  pageSize: number;
}

export interface ConversionsPage {
  data: ConversionAttempt[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

async function fetchConversions(filters: ConversionsFilters): Promise<ConversionsPage> {
  const { status, marketplace, search, dateFrom, page, pageSize } = filters;

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from("conversion_attempts")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false })
    .range(from, to);

  if (status) {
    query = query.eq("status", status);
  }

  if (marketplace) {
    query = query.eq("marketplace_code", marketplace);
  }

  if (search) {
    query = query.ilike("original_url", `%${search}%`);
  }

  if (dateFrom) {
    query = query.gte("created_at", dateFrom);
  }

  const { data, error, count } = await query;

  if (error) throw error;

  const total = count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    data: (data ?? []) as ConversionAttempt[],
    total,
    page,
    pageSize,
    totalPages,
  };
}

// ---------------------------------------------------------------------------
// Hook com filtros
// ---------------------------------------------------------------------------

export function useConversions(filters: ConversionsFilters = { page: 1, pageSize: 20 }) {
  return useQuery<ConversionsPage, Error>({
    queryKey: ["conversion-attempts", filters],
    queryFn: () => fetchConversions(filters),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Status counts (para exibir contagem por status no cabecalho)
// ---------------------------------------------------------------------------

interface StatusCount {
  status: string;
  count: number;
}

async function fetchStatusCounts(dateFrom?: string): Promise<StatusCount[]> {
  let query = supabase
    .from("conversion_attempts")
    .select("status");

  if (dateFrom) {
    query = query.gte("created_at", dateFrom);
  }

  const { data, error } = await query;
  if (error) throw error;

  const counts: Record<string, number> = {};
  for (const row of data ?? []) {
    const s = row.status ?? "unknown";
    counts[s] = (counts[s] ?? 0) + 1;
  }

  return Object.entries(counts).map(([status, count]) => ({ status, count }));
}

export function useConversionsStatusCounts(dateFrom?: string) {
  return useQuery<StatusCount[], Error>({
    queryKey: ["conversion-attempts-status-counts", dateFrom],
    queryFn: () => fetchStatusCounts(dateFrom),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Hook legado (sem filtros) - compatibilidade com LinksPage e DashboardPage
// ---------------------------------------------------------------------------

async function fetchConversionsAll(): Promise<ConversionAttempt[]> {
  const { data, error } = await supabase
    .from("conversion_attempts")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as ConversionAttempt[];
}

export function useConversionsAll() {
  return useQuery<ConversionAttempt[], Error>({
    queryKey: ["conversion-attempts-all"],
    queryFn: fetchConversionsAll,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
